import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import styled from '@emotion/styled'
import { Layout } from './components/Layout'
import Loader from './components/Loader'
import axios from 'axios'
import { updateSitemap } from './utils/sitemapGenerator'
import { getCachedResults, setCachedResults } from './utils/cacheUtils';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  margin-top: 80px;
`

const Main = styled.div`
  margin-top: 2rem;
`

const SearchContainer = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #4CAF50;
`

const SearchBox = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const Input = styled.input`
  padding: 0.8rem;
  width: 300px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const Button = styled.button`
  padding: 0.8rem 1.5rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;
  &:hover {
    background-color: #45a049;
  }
`

const RadiusSelector = styled.div`
  margin-top: 1rem;
`

const Select = styled.select`
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin-left: 0.5rem;
`

const ViewToggle = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1rem;
`

const ToggleButton = styled.button`
  padding: 0.8rem 1.5rem;
  background-color: ${props => props.active ? '#4CAF50' : 'white'};
  color: ${props => props.active ? 'white' : '#4CAF50'};
  border: 2px solid #4CAF50;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: ${props => props.active ? '#45a049' : '#f0f0f0'};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

const Content = styled.div`
  display: grid;
  grid-template-columns: ${props => props.viewMode === 'map' ? '2fr 1fr' : '1fr'};
  gap: 2rem;
  margin-top: 2rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const MapWrapper = styled.div`
  height: 600px;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  display: ${props => props.viewMode === 'list' ? 'none' : 'block'};
  overflow: hidden;
  @media (max-width: 768px) {
    height: 400px;
  }
`

const Results = styled.div`
  background-color: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  max-height: ${props => props.viewMode === 'list' ? 'none' : '600px'};
  overflow-y: auto;
`

const ResultsHeader = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;

  h2 {
    color: #4CAF50;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.9rem;
  }
`

const MarketItem = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  transition: background-color 0.3s;
  display: grid;
  gap: 1rem;
  ${props => props.viewMode === 'list' ? `
    grid-template-columns: 1fr;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    margin-bottom: 1rem;
    border: none;
  ` : `
    padding: 1rem;
    cursor: pointer;
    &:hover {
      background-color: #f5f5f5;
    }
  `}
`

const MarketHeader = styled.div`
  cursor: pointer;
`

const MarketDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  font-size: 0.9rem;
  color: #666;
  ${props => props.viewMode === 'map' && 'display: none;'}
`

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    width: 16px;
    height: 16px;
    color: #4CAF50;
  }
`

const MarketName = styled.div`
  font-weight: bold;
  color: #4CAF50;
  margin-bottom: 0.5rem;
`

const MarketAddress = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
`

const MarketActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
  ${props => props.viewMode === 'map' && 'display: none;'}
`

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.6rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #45a049;
  }

  svg {
    width: 14px;
    height: 14px;
    margin-right: 0.3rem;
  }
`

const Footer = styled.footer`
  text-align: center;
  padding: 2rem;
  background-color: white;
  margin-top: 2rem;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
`

const SearchSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`

const ErrorMessage = styled.p`
  color: #f44336;
  font-size: 1rem;
  margin-bottom: 1rem;
`

function App() {
  const [location, setLocation] = useState('')
  const [searchedLocation, setSearchedLocation] = useState('')
  const [radius, setRadius] = useState(5000)
  const [markets, setMarkets] = useState([])
  const [viewMode, setViewMode] = useState('map')
  const [isLoading, setIsLoading] = useState(false)
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [error, setError] = useState(null)
  const [searchedLocations, setSearchedLocations] = useState(() => {
    const saved = localStorage.getItem('searchedLocations');
    return saved ? JSON.parse(saved) : [];
  });

  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef([])
  const navigate = useNavigate()
  const { country, city } = useParams()
  const currentLocation = useLocation()

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center: { lat: 51.505, lng: -0.09 },
        zoom: 13
      })
    }
  }, [])

  // Handle URL parameters
  useEffect(() => {
    if (country && city) {
      const formattedCity = city.replace(/-/g, ' ')
      const formattedCountry = country.replace(/-/g, ' ')
      const searchLocation = `${formattedCity}, ${formattedCountry}`
      setLocation(searchLocation)
      
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ address: searchLocation }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const { lat, lng } = results[0].geometry.location
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter({ lat: lat(), lng: lng() })
            searchMarkets(lat(), lng())
          }
        }
      })
    }
  }, [country, city])

  useEffect(() => {
    if (country && city && !searchPerformed) {
      searchMarkets(`${city}, ${country}`);
      setSearchPerformed(true);
    }
  }, [country, city, searchPerformed]);

  // Reset searchPerformed when location changes
  useEffect(() => {
    setSearchPerformed(false);
  }, [location.pathname]);

  const updateSitemapData = async () => {
    if (searchedLocations.length === 0) return;
    
    try {
      await updateSitemap(searchedLocations);
    } catch (error) {
      // Log error but don't show to user as it's not critical for core functionality
      console.error('Sitemap update failed:', error);
    }
  };

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.setMap(null))
    markersRef.current = []
  }

  const getPlaceDetails = async (placeId) => {
      try {
        const response = await axios.get(
          `https://proxy.farmersmarketnear.me/proxy/maps/api/place/details/json?place_id=${placeId}&fields=formatted_address,formatted_phone_number,website,opening_hours,price_level&key=AIzaSyBELeWxwF81bR7LiDgY4A91WPC-DhGC8Mg`
        );

        console.log(response.data)
        if (response.data.result) {
            console.log("rest")
          return {
            formatted_address: response.data.result.formatted_address,
            formatted_phone_number: response.data.result.formatted_phone_number,
            website: response.data.result.website,
            opening_hours: response.data.result.opening_hours,
            price_level: response.data.result.price_level
          };
        } else {
          throw new Error('No details found for this place');
        }
      } catch (error) {
        console.error('Error fetching place details:', error);
        return {};
      }
    };


  const searchMarkets = async (lat, lng) => {
    setIsLoading(true);
      setError(null);

      try {
          const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBELeWxwF81bR7LiDgY4A91WPC-DhGC8Mg`;
          const response_geo = await fetch(url);
        const data = await response_geo.json();
        let city, country;

//         console.log(data.status)
//         console.log(data.results)

        if (data.status === "OK" && data.results.length > 0) {
            const location = data.results[0].address_components;

            console.log("location start")
            location.forEach(component => {
                console.log(component.types)
                console.log(component.long_name)
                if (component.types.includes("locality") || component.types.includes("postal_town")) {
                    city = component.long_name;
                    console.log(city)
                }
                if (component.types.includes("country")) {
                    country = component.long_name;
                }
            });

        }

//           const [searchCity, searchCountry] = location.split(',').map(item => item.trim());
//         const [city, country] = location.split(',').map(item => item.trim());
        const formattedLocation = `${city}, ${country}`; // Ensure correct format for caching

        // Check cache first
        const cachedResults = await getCachedResults(formattedLocation);
        if (cachedResults) {
          setMarkets(cachedResults);
          setIsLoading(false);
          navigate(`/farmers-market/${country}/${city}`);
          return;
        }


      // If no cached results, query Google Maps API
      const response = await axios.get(
        `https://proxy.farmersmarketnear.me/proxy/maps/api/place/nearbysearch/json?location=${lat},${lng}&radius=${radius}&&keyword=farmers+market&key=AIzaSyBELeWxwF81bR7LiDgY4A91WPC-DhGC8Mg`
      );

      console.log(response.data)
      const markets = await Promise.all(
          response.data.results.map(async (place) => {
            try {
              const details = await getPlaceDetails(place.place_id);
              console.log(details)
              return {
                id: place.place_id,
                name: place.name,
                lat: place.geometry.location.lat,
                lng: place.geometry.location.lng,
                address: place.vicinity,
                rating: place.rating || 'N/A',
                userRatingsTotal: place.user_ratings_total || 0,
                openNow: place.opening_hours?.open_now ?? 'Unknown',
                formattedAddress: details.formatted_address || place.vicinity,
                phoneNumber: details.formatted_phone_number || 'N/A',
                website: details.website || 'N/A',
                openingHours: details.opening_hours?.weekday_text || [],
                priceLevel: details.price_level || 'N/A',
                placeId: place.place_id,
              };
            } catch (error) {
              console.error(`Error processing place ${place.place_id}:`, error);
              return null;
            }
          })
        );

//       console.log(markets);
      // Filter out any null values (failed requests)
      const validMarkets = markets.filter(market => market !== null);


      // Set cache with the new results
        await setCachedResults(formattedLocation, markets);

        setMarkets(markets);
        setIsLoading(false);
        // Only update URL if it's different from the current URL
          if (location.pathname !== `/farmers-market/${country}/${city}`) {
            navigate(`/farmers-market/${country}/${city}`, { replace: true });
          }

    } catch (error) {
      console.error('Error searching markets:', error);
      setError('An error occurred while searching for markets. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!location) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
        // Check if we're searching for the same location
        if (location === searchedLocation) {
          // If it's the same location, just return without doing anything
          setIsLoading(false);
          return;
        }
      // Try to get cached results first
      const cachedData = await getCachedResults(location);
      
      if (cachedData) {
        // Use cached data
        setMarkets(cachedData.markets);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(cachedData.location);
        }
        setSearchedLocation(location);
        setSearchedLocations(prev => {
          if (!prev.includes(location)) {
            return [...prev, location];
          }
          return prev;
        });
      } else {
        // No cache, perform new search
        const geocoder = new google.maps.Geocoder();
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: location }, (results, status) => {
            if (status === 'OK') {
              resolve(results[0]);

            } else {
              reject(new Error('Location not found'));
            }
          });
        });

          // Extract country and city from geocoding result
          let foundCountry = ''
          let foundCity = ''

          result.address_components.forEach(component => {
            if (component.types.includes('country')) {
              foundCountry = component.long_name.toLowerCase().replace(/\s+/g, '-')
            }
            if (component.types.includes('locality')) {
              foundCity = component.long_name.toLowerCase().replace(/\s+/g, '-')
            }
          })
//           Update sitemap
          await updateSitemap(foundCountry, foundCity)


        const { lat, lng } = result.geometry.location;

        // Update URL
        navigate(`/farmers-market/${foundCountry}/${foundCity}`)
        
        // Get the city name from the geocoding result
        const cityComponent = result.address_components.find(
          component => component.types.includes('locality')
        );
        const cityName = cityComponent ? cityComponent.long_name : location;
        
        // Update states
        setSearchedLocation(cityName);
        setSearchedLocations(prev => {
          if (!prev.includes(cityName)) {
            return [...prev, cityName];
          }
          return prev;
        });

        // Search for markets
        await searchMarkets(lat(), lng());
      }
    } catch (error) {
      setError('Error finding location. Please try a different search.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      setError(null);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { data } = await axios.get(
              `https://proxy.farmersmarketnear.me/proxy/maps/api/geocode/json?latlng=${position.coords.latitude},${position.coords.longitude}&key=AIzaSyBELeWxwF81bR7LiDgY4A91WPC-DhGC8Mg`
            );
            
            if (data.results && data.results[0]) {
              const cityComponent = data.results[0].address_components.find(
                component => component.types.includes('locality')
              );
              const city = cityComponent ? cityComponent.long_name : 'your location';
              setLocation(data.results[0].formatted_address);
              setSearchedLocation(city);
              handleSearch();
            }
          } catch (error) {
            setError('Error getting location details');
            setIsLoading(false);
          }
        },
        (error) => {
          setError('Error getting your location. Please enter it manually.');
          setIsLoading(false);
        }
      );
    } else {
      setError('Geolocation is not supported by your browser');
    }
  }

  const handleMarketClick = (market) => {
    mapInstanceRef.current.setCenter({ lat: market.lat, lng: market.lng })

    mapInstanceRef.current.setZoom(16)
    const marker = new google.maps.Marker({
            position: { lat: market.lat, lng: market.lng },
            map: mapInstanceRef.current,
        });
  }

  const getGoogleMapsUrl = (market) => {
    return `https://www.google.com/maps/place/?q=place_id:${market.placeId}`
  }

  // Generate meta title and description based on location
  const getMetaData = () => {
    if (country && city) {
      const formattedCity = city.replace(/-/g, ' ')
      const formattedCountry = country.replace(/-/g, ' ')
      return {
        title: `Farmers Markets in ${formattedCity}, ${formattedCountry} | Farmers Market Finder`,
        description: `Discover local farmers markets in ${formattedCity}, ${formattedCountry}. Find fresh produce, organic food, and local vendors near you.`
      }
    }
    return {
      title: 'Find Local Farmers Markets Near You | Farmers Market Finder',
      description: 'Discover farmers markets in your area. Find fresh, local produce, organic food, and support local farmers. Use our interactive map to locate markets near you.'
    }
  }

  // Generate schema markup
  const getSchemaMarkup = () => {
    const baseSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Farmers Market Finder',
      'applicationCategory': 'LocalBusinessSearch',
      'operatingSystem': 'Web',
      'description': 'Find local farmers markets near you',
      'url': 'https://farmersmarketfinder.com'
    }

    if (markets && markets.length > 0) {
      return {
        ...baseSchema,
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'ratingCount': markets.length * 5
        },
        'offers': {
          '@type': 'AggregateOffer',
          'availabilityStarts': new Date().toISOString(),
          'category': 'Farmers Markets',
          'itemCondition': 'NewCondition'
        }
      }
    }

    return baseSchema
  }

  // Save searched locations to localStorage
  useEffect(() => {
    localStorage.setItem('searchedLocations', JSON.stringify(searchedLocations));
  }, [searchedLocations]);

  // Update sitemap when new locations are searched
  useEffect(() => {
    updateSitemapData();
  }, [searchedLocations]);

  return (
    <Layout>
      <Container>
        <Helmet>
          <title>{getMetaData().title}</title>
          <meta name="description" content={getMetaData().description} />
          <meta property="og:title" content={getMetaData().title} />
          <meta property="og:description" content={getMetaData().description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://farmersmarketfinder.com${currentLocation.pathname}`} />
          <meta property="og:image" content="https://farmersmarketfinder.com/og-image.jpg" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={getMetaData().title} />
          <meta name="twitter:description" content={getMetaData().description} />
          <meta name="twitter:image" content="https://farmersmarketfinder.com/og-image.jpg" />
          <meta name="keywords" content="farmers markets, local produce, organic food, fresh vegetables, local vendors, farm to table" />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={`https://farmersmarketfinder.com${currentLocation.pathname}`} />
          <script type="application/ld+json">
            {JSON.stringify(getSchemaMarkup())}
          </script>
        </Helmet>

        <Main>
          <SearchSection>
            <Title>
              {searchedLocation 
                ? `Local Farmers Markets in ${searchedLocation}`
                : 'Find Local Farmers Markets Near You'}
            </Title>
            <SearchBox>
              <Input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter your location"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch}>Search</Button>
              <Button onClick={handleUseLocation}>Use My Location</Button>
            </SearchBox>
            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}
          </SearchSection>

          <ViewToggle>
            <ToggleButton 
              active={viewMode === 'map'} 
              onClick={() => setViewMode('map')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Map View
            </ToggleButton>
            <ToggleButton 
              active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              List View
            </ToggleButton>
          </ViewToggle>

          <Content viewMode={viewMode}>
            <MapWrapper viewMode={viewMode}>
              <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: '8px' }} />
            </MapWrapper>

            <Results viewMode={viewMode}>
              <ResultsHeader>
                <h2>Farmers Markets</h2>
                <p>{(markets && markets.length) || 0} markets found</p>
              </ResultsHeader>
              {markets && markets.map((market) => (
                <MarketItem 
                  key={market.id} 
                  viewMode={viewMode}
                  onClick={() => handleMarketClick(market)}
                >
                  <MarketHeader>
                    <MarketName>{market.name}</MarketName>
                    <MarketAddress>{market.formattedAddress || market.address}</MarketAddress>
                  </MarketHeader>

                  {viewMode === 'list' && (
                    <>
                      <MarketDetails>
                        {market.rating && (
                          <DetailItem>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                            {market.rating} ⭐ ({market.userRatingsTotal} reviews)
                          </DetailItem>
                        )}

                        {market.openNow !== undefined && (
                          <DetailItem>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            {market.openNow ? 'Open Now' : 'Closed'}
                          </DetailItem>
                        )}

                        {market.phoneNumber && (
                          <DetailItem>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            {market.phoneNumber}
                          </DetailItem>
                        )}

                        {market.priceLevel !== undefined && (
                          <DetailItem>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="1" x2="12" y2="23"></line>
                              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                            </svg>
                            {'$'.repeat(market.priceLevel + 1)}
                          </DetailItem>
                        )}
                      </MarketDetails>

                      {market.openingHours && market.openingHours.length > 0 && (
                        <MarketDetails style={{ marginTop: '0.5rem' }}>
                          <DetailItem style={{ gridColumn: '1 / -1' }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              {market.openingHours.map((hours, idx) => (
                                <span key={idx}>{hours}</span>
                              ))}
                            </div>
                          </DetailItem>
                        </MarketDetails>
                      )}

                      <MarketActions>
                        <ActionButton 
                          href={getGoogleMapsUrl(market)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          Maps
                        </ActionButton>

                        {market.website && (
                          <ActionButton
                            href={market.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            Website
                          </ActionButton>
                        )}
                      </MarketActions>
                    </>
                  )}
                </MarketItem>
              ))}
            </Results>
          </Content>
        </Main>

        {isLoading && <Loader message="Finding farmers markets near you..." />}
      </Container>
    </Layout>
  )
}

export default App
